import {
  aliasUser,
  identifyUser,
  setUserProperties,
  trackEvent,
} from "@/lib/services";
import { syncClarityIntoMixpanel } from "@/lib/services/ClarityMixpanelBridge";
import type { Session } from "@supabase/supabase-js";
import type { CustomAuthResponse } from "./types";

// Threshold used to distinguish new OAuth signups from returning OAuth users.
// A user whose account was created within this window is treated as a signup.
const NEW_USER_WINDOW_MS = 60_000;

const getCurrentPathname = (): string =>
  typeof window !== "undefined" ? window.location.pathname : "";

// Fires Mixpanel/Clarity events for a successful OAuth (Google/LinkedIn)
// auth state change. No-op for email auth; those are handled by
// trackEmailSignIn / trackEmailSignUp below.
export function trackOAuthAuthEvent(session: Session): void {
  const provider = session.user.app_metadata?.provider;
  if (!provider || provider === "email") return;

  const createdAt = new Date(session.user.created_at).getTime();
  const isNewUser = Date.now() - createdAt < NEW_USER_WINDOW_MS;
  const pathname = getCurrentPathname();

  if (isNewUser) {
    // New OAuth signup — alias and track sign_up.
    aliasUser(session.user.id);
    identifyUser(session.user.id);
    syncClarityIntoMixpanel(session.user.id, pathname);

    const fullName =
      session.user.user_metadata?.full_name ||
      session.user.user_metadata?.name ||
      "";
    const nameParts = fullName.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    setUserProperties({
      $email: session.user.email,
      $first_name: firstName,
      $last_name: lastName,
      $name: fullName,
      $avatar:
        session.user.user_metadata?.avatar_url ||
        session.user.user_metadata?.picture,
      $created: session.user.created_at,
      user_id: session.user.id,
      user_type: session.user.user_metadata?.user_type,
      auth_provider: provider,
    });
    trackEvent("sign_up", {
      method: provider,
      email: session.user.email,
      first_name: firstName,
      last_name: lastName,
      user_id: session.user.id,
      created_at: session.user.created_at,
    });
    return;
  }

  // Returning OAuth user — track sign_in.
  identifyUser(session.user.id);
  syncClarityIntoMixpanel(session.user.id, pathname);
  setUserProperties({
    $email: session.user.email,
    $name:
      session.user.user_metadata?.full_name || session.user.user_metadata?.name,
    $avatar:
      session.user.user_metadata?.avatar_url ||
      session.user.user_metadata?.picture,
    user_id: session.user.id,
    user_type: session.user.user_metadata?.user_type,
    auth_provider: provider,
  });
  trackEvent("sign_in", {
    method: provider,
    email: session.user.email,
    user_id: session.user.id,
  });
}

// Fires Mixpanel/Clarity events for a successful email sign-in.
export function trackEmailSignIn(authData: CustomAuthResponse): void {
  identifyUser(authData.id);
  syncClarityIntoMixpanel(authData.id, getCurrentPathname());
  setUserProperties({
    $email: authData.email,
    $name: authData.userMetadata?.full_name,
    user_type: authData.userMetadata?.user_type,
    user_id: authData.id,
  });
  trackEvent("sign_in", {
    method: "email",
    email: authData.email,
    user_id: authData.id,
    user_type: authData.userMetadata?.user_type,
    full_name: authData.userMetadata?.full_name,
  });
}

export type EmailSignUpTrackingParams = {
  authData: CustomAuthResponse;
  firstName: string;
  lastName: string;
  countryId?: string;
  phoneNumber?: string;
};

// Fires Mixpanel/Clarity events for a successful email sign-up. Wraps its
// body in try/catch so tracking failures never break the signup flow.
export function trackEmailSignUp({
  authData,
  firstName,
  lastName,
  countryId,
  phoneNumber,
}: EmailSignUpTrackingParams): void {
  try {
    // alias() links the anonymous device_id to the new user.
    aliasUser(authData.id);
    identifyUser(authData.id);
    syncClarityIntoMixpanel(authData.id, getCurrentPathname());
    setUserProperties({
      $email: authData.email,
      $first_name: firstName,
      $last_name: lastName,
      $name: `${firstName} ${lastName}`.trim(),
      $created: authData.createdAt,
      user_id: authData.id,
    });
    trackEvent("sign_up", {
      email: authData.email,
      first_name: firstName,
      last_name: lastName,
      country_id: countryId,
      phone_number: phoneNumber,
      created_at: authData.createdAt,
    });
  } catch (trackingError) {
    // Non-critical: tracking failed
  }
}
